import type { FactFindAsset, FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { markAbpFundedAcquisitionAfterLiquidation } from '../../../shared/src/calculation/abpProjectionUtils.ts';
import { applyRadFundingToFactFind as applySharedRadFunding } from '../../../shared/src/calculation/support/radFunding.ts';
import {
    findAssetInFactFind,
    parseRadFundingAssetKey,
    resolveRadFundingEntry,
} from '../utils/entityFundingUtils.ts';

// The RAD funding shapes are declared alongside the implementation in shared/ and re-exported here, so
// the many frontend modules that import them from this file keep working while there is only one
// definition of what a funding bag actually is.
export type { RadFundingLiquidation, GroupedRadFunding, RadFunding } from '../../../shared/src/calculation/support/radFunding.ts';
// Imported as well as re-exported: a `export … from` re-export does not bring the names into this
// module's own scope, and the declarations below use them.
import type { RadFunding, GroupedRadFunding, RadFundingLiquidation } from '../../../shared/src/calculation/support/radFunding.ts';

export interface SavedStrategyStrategyBag {
    radFunding?: GroupedRadFunding;
    strategyOptions?: Record<string, unknown>;
    scenario?: { strategyOptions?: Record<string, unknown> };
    snapshot?: { livingExpenses?: number };
    [key: string]: unknown;
}

export interface SavedStrategyInputsBag {
    radFunding?: Record<string, number>;
    scenarioLivingExpenses?: number;
    strategyOptions?: Record<string, unknown>;
    [key: string]: unknown;
}

export interface SavedStrategyOutputsBag {
    agedCareCost?: number;
    annualContribution?: number;
    livingExpenses?: number;
    [key: string]: unknown;
}

/** A previously-saved Aged Care / Home Care strategy (sessionStorage / homeCareStrategyStorage shape). */
export interface SavedStrategy {
    id?: string;
    name?: string;
    source?: 'suggestion_engine' | 'manual' | string;
    strategy?: SavedStrategyStrategyBag;
    inputs?: SavedStrategyInputsBag;
    strategyOptions?: Record<string, unknown>;
    outputs?: SavedStrategyOutputsBag;
    [key: string]: unknown;
}

export interface AgedCareIntegrationDetails {
    id: string | undefined;
    name: string | undefined;
    source: string;
    annualCost: number;
    dailyCost: number;
    radFunding: GroupedRadFunding | null;
    radPaid: number;
    livingExpenses: number | null;
    strategyOptions: Record<string, unknown> | null;
    savedStrategy: SavedStrategy;
    outputs: SavedStrategyOutputsBag;
}

export interface HomeCareIntegrationDetails {
    id: string | undefined;
    name: string | undefined;
    annualContribution: number;
    monthlyContribution: number;
    savedStrategy: SavedStrategy;
    outputs: SavedStrategyOutputsBag;
}

export function computeTotalRadPaidFromFunding(radFunding: GroupedRadFunding | null | undefined): number {
    if (!radFunding) return 0;
    return Object.values(radFunding).flatMap((entry) => {
        if (!entry) return [];
        if (Array.isArray(entry)) return entry;
        return [entry];
    }).reduce((sum: number, item) => sum + (item.amount || 0), 0);
}

export function normalizeRadFundingFromSavedStrategy(savedStrategy: SavedStrategy | null | undefined): GroupedRadFunding | null {
    if (!savedStrategy) return null;

    if (savedStrategy.source === 'suggestion_engine') {
        const radFunding = savedStrategy.strategy?.radFunding;
        return radFunding ?? null;
    }

    const manualRadFunding = savedStrategy.inputs?.radFunding;
    if (!manualRadFunding || typeof manualRadFunding !== 'object') {
        return null;
    }

    const normalised: Record<string, RadFundingLiquidation[]> = {};

    Object.entries(manualRadFunding).forEach(([key, amount]) => {
        if (!amount) return;

        const parsed = parseRadFundingAssetKey(key);
        if (!parsed) return;

        if (!normalised[parsed.assetType]) {
            normalised[parsed.assetType] = [];
        }

        normalised[parsed.assetType].push({ id: parsed.assetId, amount });
    });

    return Object.keys(normalised).length > 0 ? normalised : null;
}

/**
 * What the plan pays toward the room up front — the deposit itself, not the assets sold for it.
 *
 * The sum of the funding is the GROSS sold: CGT comes out of it, and a sale is rounded up past the room.
 * A plan paying $817,600 up front (and $2,400 a day as DAP) was shown on the Support at Home screen as
 * "RAD paid upfront $820,000" (25 Oct 2026). The engine's row carries the deposit (`radPaidNet`, the
 * figure its fees were struck on); a hand-built plan records it in its outputs; a plan saved before
 * either falls back to the funding, never more than the room.
 */
function depositPaidBy(savedStrategy: SavedStrategy, radFunding: GroupedRadFunding | null): number {
    const row = (savedStrategy.strategy || {}) as { radPaidNet?: unknown };
    if (savedStrategy.source === 'suggestion_engine' && Number.isFinite(Number(row.radPaidNet)) && row.radPaidNet != null) return Math.max(0, Number(row.radPaidNet));
    const recorded = (savedStrategy.outputs as { radPaid?: unknown } | undefined)?.radPaid;
    if (recorded != null && Number.isFinite(Number(recorded))) return Math.max(0, Number(recorded));
    const sold = radFunding ? computeTotalRadPaidFromFunding(radFunding) : 0;
    const room = Number((savedStrategy.inputs as { radInput?: unknown } | undefined)?.radInput) || 0;
    return room > 0 ? Math.min(sold, room) : sold;
}

export function extractAgedCareIntegrationDetails(savedStrategy: SavedStrategy | null | undefined): AgedCareIntegrationDetails | null {
    if (!savedStrategy) return null;

    const radFunding = normalizeRadFundingFromSavedStrategy(savedStrategy);
    const totalRadPaid = depositPaidBy(savedStrategy, radFunding);
    const annualCost = savedStrategy.outputs?.agedCareCost || 0;

    const livingExpenses = (savedStrategy.inputs?.scenarioLivingExpenses ??
        savedStrategy.strategyOptions?.scenarioLivingExpenses ??
        savedStrategy.inputs?.strategyOptions?.scenarioLivingExpenses ??
        savedStrategy.strategy?.strategyOptions?.scenarioLivingExpenses ??
        savedStrategy.strategy?.scenario?.strategyOptions?.scenarioLivingExpenses ??
        savedStrategy.strategy?.snapshot?.livingExpenses ??
        savedStrategy.outputs?.livingExpenses ?? null) as number | null;

    return {
        id: savedStrategy.id,
        name: savedStrategy.name,
        source: savedStrategy.source || 'manual',
        annualCost,
        dailyCost: annualCost / 365,
        radFunding,
        radPaid: totalRadPaid,
        livingExpenses: livingExpenses !== undefined ? livingExpenses : null,
        strategyOptions: savedStrategy.strategyOptions || savedStrategy.strategy?.strategyOptions || null,
        savedStrategy,
        outputs: savedStrategy.outputs || {}
    };
}

export function extractHomeCareIntegrationDetails(savedStrategy: SavedStrategy | null | undefined): HomeCareIntegrationDetails | null {
    if (!savedStrategy) return null;

    const annualContribution = savedStrategy.outputs?.annualContribution || 0;

    return {
        id: savedStrategy.id,
        name: savedStrategy.name,
        annualContribution,
        monthlyContribution: annualContribution / 12,
        savedStrategy,
        outputs: savedStrategy.outputs || {}
    };
}

/**
 * Liquidates the nominated assets to pay the RAD — re-exported from `shared/`, which now holds the one
 * implementation.
 *
 * The body used to live here, and a verbatim port of it lived in the server's suggestion engine. The
 * two had drifted (one produced a `NaN` balance where the other produced a number), which is the sort
 * of thing two copies of the same arithmetic do. Re-exported rather than deleted outright because the
 * Support-at-Home funding panels import it from this module, and one of them does so from a synchronous
 * derivation where a server round-trip would not fit.
 */
export function applyRadFundingToFactFind(
    factFind: DraftFactFind,
    radFunding: RadFunding | null | undefined,
    cgtOverrides: Record<string, number> = {},
): DraftFactFind {
    return applySharedRadFunding(factFind as unknown as FactFind, radFunding, cgtOverrides) as unknown as DraftFactFind;
}

export interface LiquidateChosenAssetsResult {
    factFind: DraftFactFind;
    net: number;
    gross: number;
    cgt: number;
}

/**
 * Liquidate a user-chosen set of assets to fund a cash outlay, CGT-aware, WITHOUT recording a RAD.
 * Reduces each chosen asset by its gross liquidation amount (shares net of estimated CGT) and returns the
 * NET cash raised. Used where the client picks which assets to sell — granny-flat (cash paid to family) and
 * the downsizer "buy a dearer home" top-up — so those modules match the Aged Care RAD / RV entry-fee pickers.
 * @param fundingMap - manual keys `type-id` → gross liquidation amount
 */
export function liquidateChosenAssets(
    factFind: DraftFactFind,
    fundingMap: Record<string, number> | null | undefined,
    cgtOverrides: Record<string, number> = {},
): LiquidateChosenAssetsResult {
    const modified = structuredClone(factFind);
    if (!fundingMap) return { factFind: modified, net: 0, gross: 0, cgt: 0 };
    let net = 0, gross = 0, cgt = 0;
    Object.entries(fundingMap).forEach(([assetKey, amount]) => {
        const parsed = parseRadFundingAssetKey(assetKey);
        if (!parsed) return;
        const entry = resolveRadFundingEntry(factFind, assetKey, amount, cgtOverrides);
        if (entry.gross <= 0) return;
        const located = findAssetInFactFind(modified, parsed.assetType, parsed.assetId);
        if (!located) return;
        const asset = located.asset;
        asset.value = (asset.value || 0) - entry.gross;
        if (parsed.assetType === 'accountBasedPensions') {
            markAbpFundedAcquisitionAfterLiquidation(asset as FactFindAsset, modified as unknown as FactFind, 1);
        }
        net += entry.net; gross += entry.gross; cgt += entry.cgt;
    });
    return { factFind: modified, net, gross, cgt };
}
